import { faTrashCan } from '@fortawesome/pro-light-svg-icons'
import { useCallback, useMemo } from 'react'
import { useNavigate, useLocation } from 'react-router'
import { ButtonDisabledOrLoader } from '../../../../../../../../../../../baseComponents/Button'
import PermissionChecker from '../../../../../../../../../../../baseComponents/PermissionChecker'
import { TwinIconToolTip } from '../../../../../../../../../../../baseComponents/TwinIcon'
import { OrderData } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/types'
import twinFetchPost from '../../../../../../../../../../../utils/globals/data'
import { getBasePath } from '../../../../../../../../../../../utils/globals/link'
import useEditDeleteModal from '../../../../../../../../../../../utils/hooks/useEditDeleteModal'
import useTwinTranslation from '../../../../../../../../../../../utils/hooks/useTwinTranslation'
import { getUserInfo, getUserPermissions } from '../../../../../../../../../../../utils/reducers/getters'
import { SellDownloadButtonCSV, SellDownloadButtonPDF, SellDownloadPdfTemplateButton, SellDownloadTags, SellPdfPreview, SellQuickAccessButton } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderSellLayout/SellButtons'
import { checkSellFromOrderAllOk } from '../../../../../../../../../../../utils/globals/order/sell'
import { SellOrderDuplicateButton, SellOrderRecalculateButton } from '../../../SellOrderButtons'
import ModalDeleteSellOrderDraft from './ModalDeleteSellOrderDraft'
import { ButtonOrderInfoLeftBase, ButtonOrderInfoLeftBaseProps, OrderDivideIntoChaptersButton, OrderProductTypesButton, OrderVariablesButton } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtons'
import ModalUpdateWorkSheetProducts from './ModalUpdateWorkSheetProducts'
import { OrderStepButtonsBase, OrderStepButtonsBaseComponentProps } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent/types'
import { OrderStepButtonsDefaultBaseComponent, OrderStepButtonsNewStyleBaseComponent } from '../../../../../../../../../../../specificComponents/inputTableLayouts/order/OrderLayout/OrderButtonsBaseComponent'
import useIsLoading from '../../../../../../../../../../../utils/hooks/useIsLoading'

const SellOrderCreateStepButtons: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    const { t } = useTwinTranslation()
    const { infoMessageQuote } = useSellOrderCreateStepButtonsLogic({ data, onSubmit, ...rest })
    const OrderId = data?.header?.id
    return (
        <div className='flex items-center relative gap-5 flex-wrap'>
            <OrderStepButtonsDefaultBaseComponent {...{ data, onSubmit, hookFunction: useSellOrderCreateStepButtonsLogic, ...rest }} className='mr-8' />
            <SellOrderProcessButton title={t('processOrder', 'Procesar pedido')} disabled={infoMessageQuote ? true : false} OrderId={OrderId} onSubmit={onSubmit} status={data?.header.status} infoMessage={infoMessageQuote || ''} />
        </div>
    )
}

const useSellOrderCreateStepButtonsLogic = ({ data, onSubmit, orderHeaderURLs, setCompleteData, updateHeaderData, userPermissions }: OrderStepButtonsBaseComponentProps) => {
    const { t } = useTwinTranslation()
    const name = 'sell_order_create_step_buttons'
    let infoMessageQuote = checkSellFromOrderAllOk(data, 'sellOrder', t)
    const Order = data?.header
    const userInfo = getUserInfo()
    const EmployeeId = userInfo?.Employee?.id
    const orderEmployeeId = Order?.CreateEmployeeId
    const isMyQuote = orderEmployeeId === EmployeeId
    const hideSellQuickAccesButton = !Order?.QuoteOrderId && !Order?.SellOrderId && !Order?.SellDeliveryNoteId && !Order?.SellInvoiceId && !Order?.WorkSheetId && !Order?.relatedSellOrders?.length && !Order?.relatedSellDeliveryNotes?.length && !Order?.relatedSellInvoices?.length && !Order?.relatedWorkSheets?.length

    const defaultButtons = [{ "id": "previewSellOrder" }, { "id": "downloadSellOrder" }, { "id": "quickAccess" }, { "id": "duplicateSell" }, { "id": "deleteSellOrder" }]

    const sellOrderDuplicateOrderText = t('duplicateOrder', 'Duplicar factura')
    const sellOrderDeleteDraftText = t('deleteDraft', 'Eliminar borrador')
    const sellOrderRecalculateOrderText = t('recalculateSellOrder', 'Recalcular pedido')
    const sellOrderDownloadCSVText = t('downloadCsv', 'Descargar csv')
    const sellOrderPrintTagsText = t('printTags', 'Imprimir etiquetas')
    const sellOrderQuickAccessText = t('quickAccess', 'Acceso rápido')
    const sellOrderPreviewPDFText = t('previewPdf', 'Previsualizar PDF')
    const sellOrderDownloadPDFTemplateText = t('downloadPdfTemplate', 'Descargar plantilla PDF')
    const sellOrderDownloadPDFText = t('downloadPdf', 'Descargar PDF')
    const sellOrderDivideIntoChaptersText = t('divideIntoChapters', 'Dividir en capítulos')
    const sellOrderVariablesText = t('variables', 'Variables')
    const sellOrderProductTypesText = t('valueProductTypes', 'Valor tipos de producto')

    const ButtonComponents: OrderStepButtonsBase = useMemo(() => {
        return {
            duplicateSell: {
                component: SellOrderDuplicateButton,
                extraProps: {
                    title: sellOrderDuplicateOrderText,
                    id: 'duplicateSell',
                    allRowData: data,
                }
            },
            deleteSellOrder: {
                component: SellOrderDeleteButton,
                showButton: isMyQuote,
                extraProps: {
                    title: sellOrderDeleteDraftText,
                    id: 'deleteSellOrder',
                    allRowData: data,
                }
            },
            recalculateSellOrder: {
                component: SellOrderRecalculateButton,
                extraProps: {
                    title: sellOrderRecalculateOrderText,
                    id: 'recalculateSellOrder',
                    allRowData: data,
                    onSubmit,
                }
            },
            downloadSellCsv: {
                component: SellDownloadButtonCSV,
                showButton: !infoMessageQuote,
                extraProps: {
                    title: sellOrderDownloadCSVText,
                    id: 'downloadSellCsv',
                    Order,
                    typeTemplate: 'sellOrder',
                }
            },
            downloadSellTag: {
                component: SellDownloadTags,
                showButton: !infoMessageQuote,
                extraProps: {
                    title: sellOrderPrintTagsText,
                    id: 'downloadSellTag',
                    Order,
                    typeTemplate: 'sellOrder',
                }
            },
            quickAccess: {
                component: SellQuickAccessButton,
                showButton: !hideSellQuickAccesButton,
                extraProps: {
                    title: sellOrderQuickAccessText,
                    id: 'quickAccess',
                    Order,
                }
            },
            previewSellOrder: {
                component: SellPdfPreview,
                showButton: !infoMessageQuote,
                extraProps: {
                    title: sellOrderPreviewPDFText,
                    id: 'previewSellOrder',
                    Order,
                    typeTemplate: 'sellOrder',
                }
            },
            downLoadSellOrderTemplate: {
                component: SellDownloadPdfTemplateButton,
                showButton: !infoMessageQuote,
                extraProps: {
                    title: sellOrderDownloadPDFTemplateText,
                    id: 'downLoadSellOrderTemplate',
                    Order,
                    typeTemplate: 'sellOrder',
                    key: 'downloadSellOrderPDFTemplate'
                }
            },
            downloadSellOrder: {
                component: SellDownloadButtonPDF,
                showButton: !infoMessageQuote,
                extraProps: {
                    title: sellOrderDownloadPDFText,
                    id: 'downloadSellOrder',
                    Order,
                    typeTemplate: 'sellOrder',
                    key: 'downloadSellOrder'
                }
            },
            divideIntoChaptersQuote: {
                component: OrderDivideIntoChaptersButton,
                extraProps: {
                    title: sellOrderDivideIntoChaptersText,
                    id: 'divideIntoChaptersQuote',
                    allRowData: data,
                    userPermissions,
                    onSubmit,
                    orderHeaderURLs,
                    setCompleteData,
                    updateHeaderData
                }
            },
            variablesQuote: {
                component: OrderVariablesButton,
                extraProps: {
                    title: sellOrderVariablesText,
                    id: 'variablesQuote',
                    allRowData: data,
                    userPermissions,
                    onSubmit,
                    orderHeaderURLs,
                    setCompleteData,
                    updateHeaderData
                }
            },
            sellProductTypes: {
                component: OrderProductTypesButton,
                extraProps: {
                    title: sellOrderProductTypesText,
                    id: 'sellProductTypes',
                    allRowData: data,
                    userPermissions,
                    onSubmit,
                    orderHeaderURLs,
                    setCompleteData,
                    updateHeaderData,
                }
            },
        }
    }, [sellOrderDuplicateOrderText, sellOrderDeleteDraftText, sellOrderRecalculateOrderText, isMyQuote, data, sellOrderDownloadCSVText, Order, hideSellQuickAccesButton, infoMessageQuote, onSubmit, sellOrderDownloadPDFTemplateText, sellOrderDownloadPDFText, sellOrderPreviewPDFText, sellOrderPrintTagsText, sellOrderQuickAccessText, orderHeaderURLs, sellOrderDivideIntoChaptersText, sellOrderVariablesText, setCompleteData, updateHeaderData, userPermissions, sellOrderProductTypesText])

    return { infoMessageQuote, name, ButtonComponents, defaultButtons }
}

export const SellOrderCreateStepButtonsNew: React.FC<OrderStepButtonsBaseComponentProps> = ({ data, onSubmit, ...rest }) => {
    const { t } = useTwinTranslation()
    const { infoMessageQuote } = useSellOrderCreateStepButtonsLogic({ data, onSubmit, ...rest })
    const OrderId = data?.header?.id
    return (
        <div className='flex order_config_buttons_parent items-center relative gap-5 flex-wrap'>
            <OrderStepButtonsNewStyleBaseComponent {...{ data, onSubmit, hookFunction: useSellOrderCreateStepButtonsLogic, ...rest }} className='mr-8' />
            <SellOrderProcessButton title={t('processOrder', 'Procesar pedido')} disabled={infoMessageQuote ? true : false} OrderId={OrderId} onSubmit={onSubmit} status={data?.header.status} infoMessage={infoMessageQuote || ''} />
        </div>
    )
}

type SellOrderProcessButtonProps = SellOrderProcessButtonLogicProps & ButtonOrderInfoLeftBaseProps & {
    title: string
    children?: React.ReactNode
}

const SellOrderProcessButton: React.FC<SellOrderProcessButtonProps> = ({ title, OrderId, onSubmit, status, disabled, infoMessage, ...rest }) => {
    const { finalizeOrder, openModal, setOpenModal, loading } = useSellOrderProcessButtonLogic({ OrderId, onSubmit, status })
    return (
        <ButtonOrderInfoLeftBase disabled={disabled} infoMessage={infoMessage} {...rest}>
            <div className='min-w-175'>
                <ButtonDisabledOrLoader onClick={finalizeOrder} buttonIsDisabled={disabled} loading={loading} textButton={title} className='w-full' />
            </div>
            {openModal?.type === 'create' && OrderId ? <ModalUpdateWorkSheetProducts setOpenModal={setOpenModal} OrderId={OrderId} onSubmit={onSubmit}/> : null}
        </ButtonOrderInfoLeftBase>
    )
}

interface SellOrderProcessButtonLogicProps {
    onSubmit: () => void
    OrderId?: string
    status: number
}

const useSellOrderProcessButtonLogic = ({ onSubmit, OrderId, status }: SellOrderProcessButtonLogicProps) => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    const { startLoading, endLoading, loading } = useIsLoading()
    const finalizeOrder = useCallback(async () => {
        startLoading?.()
        const response = await twinFetchPost('/api/app/order/sell/sellOrder/updateSellOrder', { id: OrderId, status: status + 1 })
        if (response.status === 200) {
            onSubmit()
        } else {
            const responseJSON = await response.json()
            if (responseJSON?.errors?.[0]?.msg === 'workSheetProductsToUpdate') {
                setOpenModal({ type: 'create', allRowData: {} })
            }
        }
        endLoading?.()
    }, [OrderId, onSubmit, status, setOpenModal, startLoading, endLoading])

    return { finalizeOrder, openModal, setOpenModal, loading }
}

interface SellOrderDeleteButtonProps {
    allRowData: OrderData
    title: string
    children?: React.ReactNode
    id: string
}

const SellOrderDeleteButton: React.FC<SellOrderDeleteButtonProps> = ({ allRowData, id, title, children }) => {
    const { openModal, setOpenModal, myOnSubmit } = useSellOrderDeleteButtonLogic()
    return (
        <PermissionChecker permission='delete' userPermissions={getUserPermissions('sell.order')}>
            <div onClick={() => setOpenModal({ type: 'delete', allRowData })}>
                {children ?? <TwinIconToolTip classNameIcon='hover:text-red-BA regular22' message={title} icon={faTrashCan} id={id} />}
            </div>
            {openModal?.type === 'delete' ? <ModalDeleteSellOrderDraft setOpened={() => setOpenModal(null)} allRowData={allRowData} onSubmit={myOnSubmit}/> : null}
        </PermissionChecker>
    )
}

const useSellOrderDeleteButtonLogic = () => {
    const { openModal, setOpenModal } = useEditDeleteModal()
    const navigate = useNavigate()
    const location = useLocation()
    const basePath = getBasePath(location.pathname, 'sellOrders')

    const myOnSubmit = useCallback(() => {
        navigate(basePath)
    }, [navigate, basePath])
    return { openModal, setOpenModal, myOnSubmit }
}

export default SellOrderCreateStepButtons